import { useQuery } from "@tanstack/react-query";
import { requestURLs } from "../../Environment";
import { QUERY_KEYS } from "./querykeys";
import {
  AxiosResponseAndError,
  ResponseType,
} from "../types/axiosResponseType";
import ApiRequest from "../ApiRequest";
import {
  OverAllTrackEnumValue,
  ProjectDefaultViewEnumValue,
  ProjectStatusEnumValue,
  ScheduleAndBudgetTrend,
  UserRoleEnumValue,
} from "@backend/src/schemas/enums";
import { UserOrganisationType } from "./useCurrentUserQuery";
import { Task } from "../mutation/useTaskCreateMutation";
import { ProjectCategory, ProjectPhase, ProjectPriority, ProjectType, Workstream } from "@/components/project/CreateProjectNoPopupForm";

export type Project = {
  spi?: number;
  forecastBudgetVariance?: number | null;
  forecastVariance?: number | null;
  budgetStatusFlag?: string | null;
  overallTrack: keyof typeof OverAllTrackEnumValue;
  projectId: string;
  assignedUsers: AssignedUsers[];
  organisationId: string;
  projectName: string;
  projectDescription: string;
  startDate: string;
  estimatedEndDate: string;
  status: keyof typeof ProjectStatusEnumValue;
  defaultView: keyof typeof ProjectDefaultViewEnumValue;
  timeTrack: string | null;
  budgetTrack: string | null;
  estimatedBudget: string;
  actualCost: string | null;
  progressionPercentage: number | string | null;
  createdAt: string;
  updatedAt: string;
  createdByUser: CreatedByUser;
  createdByUserId: string;
  currency: string;
  projectManagerInfo: { user: CreatedByUser }[];
  scheduleTrend: keyof typeof ScheduleAndBudgetTrend;
  budgetTrend: keyof typeof ScheduleAndBudgetTrend;
  consumedBudget: string;
  reCalculateBudget: string;
  actualEndDate: string;
  actualStartDate: string;
  estimatedDuration: number;
  actualDuration: number;
  isActive: boolean;
  portfolio: Portfolio | null;
  program: Program | null;
  reCalculateEndDate?: Date | null;
  isMilestone?: boolean;
  reCalculatedDuration: number | null;
  tasks: Task[];
  expectedprogress?: number;
  projectCode?: string | null;
  sponsor?: string | null;
  clientDepartment?: string | null;
  projectPhase?: ProjectPhase | string | null;
  priority?: ProjectPriority | string | null;
  projectType?: ProjectType | string | null;
  projectCategory?: ProjectCategory | string | null;
  tags?: string[] | null;
  includeInTimesheet?: boolean;
  workstreamId?: string | null;
  workstream?: Workstream | null;
  documents?: any[] | null;
  ganttColor?: string | null;
};

export interface AssignedUsers {
  projectAssignUsersId: string;
  projectId: string;
  assginedToUserId: string;
  projectRole: string | null;
  projectJobTitle: string | null;
  createdAt: Date;
  updatedAt: Date;
  user: User;
}

export interface User {
  userId: string;
  email: string;
  status: string;
  firstName: string | null;
  lastName: string | null;
  timezone: null;
  country: null;
  avatarImg: null;
  isVerified: boolean;
  createdAt: Date;
  updatedAt: Date;
  userOrganisation: UserOrganisationType[];
  userAvailability?: { startDate: string; endDate?: string | null }[];
}

export interface UserOrganisation {
  role: keyof typeof UserRoleEnumValue;
}
export interface CreatedByUser {
  firstName: string | null;
  lastName: string | null;
  email: string;
  avatarImg: null;
}
export interface Portfolio {
  portfolioId: string;
  organisationId: string;
  portfolioName: string;
}
export interface Portfolio {
  portfolioId: string;
  organisationId: string;
  portfolioName: string;
}
export interface Program {
  programId: string;
  portfolioId: string;
  programName: string;
}

type ProjectApiResponse = ResponseType<Project[]>;

function useProjectQuery(enabled = false, query: Record<string, any> = {}) {
  const projectQuery = useQuery<
    AxiosResponseAndError<ProjectApiResponse>["response"],
    AxiosResponseAndError<ProjectApiResponse>["error"]
  >({
    queryKey: [QUERY_KEYS.getProjects],
    queryFn: async () => {
      const response = await ApiRequest.get(requestURLs.project, { params: query });
      return response;
    },
    enabled,
    placeholderData: (prev) => prev,
  });


  return projectQuery;
}

export default useProjectQuery;
